import { decode } from 'html-entities';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useStores } from '../../stores';
import { detectBrowser, formatLocale, getFontSize } from '../../utilities';
import ButtonBase from '../ButtonBase';
import ChevronLeftBold from '../Icons/ChevronLeftBold';
import ChevronRightBold from '../Icons/ChevronRightBold';
import List from '../List';
import ListItem from '../ListItem';
import ListItemButton from '../ListItemButton/ListItemButton';
import TagBlock from '../TagBlock';

const RelatedQuestionsList = ({ subheader, content, ...props }) => {
  const { bot, config } = useStores();
  const { t, i18n } = useTranslation();
  const ref = React.useRef();
  const textColor = '#2873c0';

  const defaultRelatedQuestionsCount = config.relatedQuestions.defaultCount || 3;
  const maxRelatedQuestionsCount = config.relatedQuestions.maxCount || 5;

  const [visibleItemsCount, setVisibleItemsCount] = React.useState(defaultRelatedQuestionsCount);

  const isCrossAgencyResponse = content[0].ProjectId !== config.id;
  const isShowButton = content.length > defaultRelatedQuestionsCount;
  const allListItems =
    content.length < maxRelatedQuestionsCount ? content.length : maxRelatedQuestionsCount;

  const textFontSize = getFontSize(config.message);

  React.useEffect(() => {
    if (!detectBrowser.isIE && ref && visibleItemsCount) {
      ref.current.scrollIntoView({ behaviour: 'smooth' });
    }
  }, [visibleItemsCount]);

  const handleClick = event => {
    if (!ref.current || !ref.current.contains(event.target)) {
      return;
    }
    setVisibleItemsCount(
      visibleItemsCount === allListItems ? defaultRelatedQuestionsCount : allListItems
    );
  };

  return (
    <>
      <List subheader={subheader}>
        <Root ref={ref} {...props}>
          {content
            .slice(0, visibleItemsCount)
            .map(({ Message: MessageText, MesGroupId: MesId, ProjectId, ProjectName }, idx) => (
              <ListItem key={idx}>
                <ListItemButton
                  type="button"
                  onClick={() => {
                    localStorage.setItem('rightQuestionClicked', true);

                    bot.runInfoSequence({
                      ProjectId,
                      MesId,
                      MessageText,
                      SourceLanguage: formatLocale(i18n.language),
                      CrossProjectName: isCrossAgencyResponse ? ProjectName : ''
                    });
                  }}
                  textFontSize={textFontSize}
                >
                  <QuestionText isCrossAgencyResponse={isCrossAgencyResponse} textColor={textColor}>
                    {isCrossAgencyResponse && <TagBlock>{ProjectName}</TagBlock>}
                    {decode(MessageText)}
                  </QuestionText>
                </ListItemButton>
              </ListItem>
            ))}
          {isShowButton && (
            <Button
              onClick={e => handleClick(e)}
              svgMargin={visibleItemsCount !== defaultRelatedQuestionsCount}
              textColor={textColor}
              textFontFamily={config.fontFamily}
              textFontSize={textFontSize}
            >
              {visibleItemsCount === allListItems ? (
                <>
                  <ChevronLeftBold fill={textColor} /> {t('viewLess')}
                </>
              ) : (
                <>
                  {t('viewAll')} <ChevronRightBold fill={textColor} />
                </>
              )}
            </Button>
          )}
        </Root>
      </List>
    </>
  );
};

RelatedQuestionsList.propTypes = {
  subheader: PropTypes.node,
  content: PropTypes.arrayOf(
    PropTypes.shape({
      ProjectId: PropTypes.number.isRequired
    })
  )
};

const Root = styled.div``;

const Button = styled(ButtonBase)`
  margin-top: 10px;
  font-family: ${props => props.textFontFamily};
  font-size: ${props => props.textFontSize}px;
  font-weight: 700;
  color: ${props => props.textColor};

  & svg {
    width: 10px;
  }

  ${({ svgMargin }) => {
    if (svgMargin) {
      return css`
        & svg {
          margin-right: 4px;
          margin-left: 0;
        }
      `;
    }
    return css`
      & svg {
        margin-right: 0;
        margin-left: 4px;
      }
    `;
  }}
`;

const QuestionText = styled.div`
  position: relative;
  color: ${props => props.textColor};
  transition: color 0.2s;
  &:hover {
    color: #0d457f;
    &:before {
      background: #0d457f;
    }
  }
  ${({ isCrossAgencyResponse }) => {
    if (!isCrossAgencyResponse) {
      return css`
        padding-left: 12px;
        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: 5px;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: ${props => props.textColor};
        }
      `;
    }
    return null;
  }}
`;

export default RelatedQuestionsList;
